import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';

import { ContactDirectoryService } from '../../../services/contact-directory/contact-directory.service';
import { CountriesService } from '../../../services/countries/countries.service';

@Component({
  selector: 'app-contact-form-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './contact-form-dialog.component.html',
  styleUrls: ['./contact-form-dialog.component.css'],
})
export class ContactFormDialogComponent implements OnInit {
  isSubmitting: boolean = false;
  operatorTypes: any[] = [];

  selectedCountryCode: string = '+91';
  countries: any[] = [];

  // Contributor image upload (optional, max 2MB)
  contributorImagePreview: string | null = null;
  contributorImageFile: File | null = null;
  operatorLogoPreview: string | null = null;
  operatorLogoFile: File | null = null;
  isDragOver = false;
  isOperatorLogoDragOver = false;
  private readonly CONTRIBUTOR_IMAGE_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
  readonly CONTRIBUTOR_IMAGE_ACCEPT =
    'image/jpeg,image/png,image/webp,image/gif';

  // Form properties
  formData = {
    name: '',
    friendly_name: '',
    email: '',
    phone: '',
    address: '',
    operator: '',
    operator_type: '',
    request_reason: 'Updating contact information with latest details',
  };

  // Validation errors
  errors = {
    name: '',
    friendly_name: '',
    email: '',
    phone: '',
    address: '',
    operator: '',
    operator_type: '',
    contributorImage: '',
    operatorLogo: '',
    request_reason: '',
  };

  constructor(
    private dialogRef: MatDialogRef<ContactFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private contactDirectoryService: ContactDirectoryService,
    private countriesService: CountriesService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadOperatorTypes();
    this.countries = this.countriesService.getAllCountries();

    // Check if we're in edit mode and pre-populate the form
    // Use setTimeout to ensure countries are loaded and view is ready
    if (this.data && this.data.isEditMode && this.data.contribution) {
      setTimeout(() => {
        this.populateFormForEdit(this.data.contribution);
        this.cdr.detectChanges();
      }, 0);
    } else if (
      this.data?.isEditMode &&
      this.data?.submissionId &&
      this.data?.prefillFromDirectory
    ) {
      // Directory drawer edit: use prefill (same API flow as contribution edit)
      this.applyPrefillFromDirectory(this.data.prefillFromDirectory);
    } else if (this.data?.prefillFromDirectory) {
      this.applyPrefillFromDirectory(this.data.prefillFromDirectory);
    }
  }

  /** Prefill from directory drawer row (edit flow before API wiring). */
  private applyPrefillFromDirectory(p: {
    name?: string;
    friendly_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    operator?: string;
    operator_type?: string;
  }) {
    this.formData.name = (p.name ?? '').trim();
    this.formData.friendly_name = '';
    this.formData.email = this.normalizeEmails(p.email ?? '');
    this.applyPhonePrefill(p.phone ?? '');
    this.formData.address = (p.address ?? '').trim();
    this.formData.operator = (p.operator ?? '').trim();
    this.formData.operator_type = (p.operator_type ?? '').trim();
  }

  populateFormForEdit(contribution: any) {
    const rowData = contribution.row_data || {};

    // Populate form fields
    this.formData.name = (rowData.name || '').toString().trim();
    this.formData.friendly_name = '';
    this.formData.email = this.normalizeEmails(rowData.email || '');
    this.formData.address = rowData.address || '';
    this.formData.operator = rowData.operator || '';
    this.formData.operator_type = rowData.operator_type || '';
    this.applyPhonePrefill(rowData.phone || '');
  }

  loadOperatorTypes() {
    this.contactDirectoryService.getOperatorTypes().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          // Handle different possible response structures
          if (Array.isArray(response.data)) {
            this.operatorTypes = response.data;
          } else if (
            response.data.operator_types &&
            Array.isArray(response.data.operator_types)
          ) {
            this.operatorTypes = response.data.operator_types;
          } else if (response.data.rows && Array.isArray(response.data.rows)) {
            this.operatorTypes = response.data.rows;
          } else {
            this.operatorTypes = [];
          }
        } else {
          this.operatorTypes = [];
        }
        this.alignOperatorTypeWithOptions();
      },
      error: (error: any) => {
        console.error('Error loading operator types:', error);
        this.operatorTypes = [];
      },
    });
  }

  getOperatorTypeName(operatorType: any): string {
    // Handle different possible structures from API
    if (typeof operatorType === 'string') {
      return operatorType;
    }
    return (
      operatorType?.name ||
      operatorType?.operator_type ||
      operatorType?.type ||
      operatorType?.value ||
      'N/A'
    );
  }

  getOperatorTypeValue(operatorType: any): string {
    // Return the value to be stored
    if (typeof operatorType === 'string') {
      return operatorType;
    }
    return (
      operatorType?.name ||
      operatorType?.operator_type ||
      operatorType?.type ||
      operatorType?.value ||
      ''
    );
  }

  private alignOperatorTypeWithOptions(): void {
    const current = (this.formData.operator_type || '').trim();
    if (!current || this.operatorTypes.length === 0) {
      return;
    }

    const match = this.operatorTypes.find((ot) => {
      const value = this.getOperatorTypeValue(ot).trim();
      return value.toLowerCase() === current.toLowerCase();
    });

    if (match) {
      this.formData.operator_type = this.getOperatorTypeValue(match);
    }
  }

  validateName(value: string): string {
    if (!value || value.trim().length === 0) {
      return 'Nodal officer name is required';
    }
    if (value.trim().length < 2) {
      return 'Nodal officer name must be at least 2 characters long';
    }
    if (value.trim().length > 100) {
      return 'Nodal officer name must not exceed 100 characters';
    }
    if (!/^[a-zA-Z\s.'-]+$/.test(value.trim())) {
      return "Nodal officer name can only contain letters, spaces, and special characters (. ' -)";
    }
    return '';
  }

  validateEmail(value: string, isRequired: boolean = false): string {
    const normalizedEmail = this.normalizeEmails(value);
    if (!normalizedEmail || normalizedEmail.trim().length === 0) {
      return isRequired ? 'Email is required' : '';
    }
    const emails = normalizedEmail
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (emails.length === 0) {
      return isRequired ? 'At least one email address is required' : '';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return `"${email}" is not a valid email address`;
      }
      if (email.length > 100) {
        return `Email address must not exceed 100 characters`;
      }
    }
    return '';
  }

  validatePhone(value: string, isRequired: boolean = false): string {
    const normalizedPhone = this.normalizePhonesForInput(value);
    if (!normalizedPhone || normalizedPhone.trim().length === 0) {
      return isRequired ? 'Mobile number is required' : '';
    }
    const phones = normalizedPhone
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (phones.length === 0) {
      return isRequired ? 'At least one mobile number is required' : '';
    }
    for (const phone of phones) {
      const hasCountryCode = phone.startsWith('+');
      const cleaned = phone.replace(/\D/g, '');
      if (hasCountryCode) {
        if (!/^\+\d{8,15}$/.test(phone)) {
          return `"${phone}" must be a valid international number`;
        }
      } else if (cleaned.length < 7 || cleaned.length > 15) {
        return `"${phone}" must be between 7 and 15 digits`;
      }
    }
    return '';
  }

  validateEmailOrPhone(): string {
    const hasEmail =
      this.formData.email && this.formData.email.trim().length > 0;
    const hasPhone =
      this.formData.phone && this.formData.phone.trim().length > 0;

    if (!hasEmail && !hasPhone) {
      return 'Either Email or Mobile Number is required';
    }
    return '';
  }

  validateAddress(value: string): string {
    if (!value || value.trim().length === 0) {
      return 'Address is required';
    }
    if (value.trim().length < 5) {
      return 'Address must be at least 5 characters long';
    }
    if (value.trim().length > 200) {
      return 'Address must not exceed 200 characters';
    }
    return '';
  }

  validateOperator(value: string): string {
    if (!value || value.trim().length === 0) {
      return 'Operator Name is required';
    }
    if (value.trim().length < 2) {
      return 'Operator must be at least 2 characters long';
    }
    if (value.trim().length > 100) {
      return 'Operator must not exceed 100 characters';
    }
    return '';
  }

  validateOperatorType(value: string): string {
    if (!value || value.trim().length === 0) {
      return 'Operator type is required';
    }
    return '';
  }

  private isUpdateRequestMode(): boolean {
    return !!(this.data?.isEditMode && this.data?.submissionId);
  }

  validateRequestReason(value: string, isRequired: boolean = false): string {
    const v = (value ?? '').toString().trim();
    if (!v) {
      return isRequired ? 'Reason for update is required' : '';
    }
    if (v.length < 5) {
      return 'Reason must be at least 5 characters';
    }
    if (v.length > 1000) {
      return 'Reason must not exceed 1000 characters';
    }
    return '';
  }

  validateFriendlyName(value: string): string {
    if (!value || value.trim().length === 0) {
      return 'Friendly name is required';
    }
    if (value.trim().length < 2) {
      return 'Friendly name must be at least 2 characters long';
    }
    if (value.trim().length > 100) {
      return 'Friendly name must not exceed 100 characters';
    }
    return '';
  }

  onFieldBlur(fieldName: keyof typeof this.formData) {
    const value = this.formData[fieldName];
    switch (fieldName) {
      case 'name':
        this.errors.name = this.validateName(value);
        break;
      case 'friendly_name':
        this.errors.friendly_name = this.validateFriendlyName(value);
        break;
      case 'email':
        // Email is not required individually, but check format if provided
        this.errors.email = this.validateEmail(value, false);
        // Also validate the email-or-phone requirement
        const emailOrPhoneError = this.validateEmailOrPhone();
        if (emailOrPhoneError && !this.formData.phone?.trim()) {
          this.errors.email = emailOrPhoneError;
        }
        break;
      case 'phone':
        // Phone is not required individually, but check format if provided
        this.errors.phone = this.validatePhone(value, false);
        // Also validate the email-or-phone requirement
        const phoneOrEmailError = this.validateEmailOrPhone();
        if (phoneOrEmailError && !this.formData.email?.trim()) {
          this.errors.phone = phoneOrEmailError;
        }
        break;
      case 'address':
        this.errors.address = this.validateAddress(value);
        break;
      case 'operator':
        this.errors.operator = this.validateOperator(value);
        break;
      case 'operator_type':
        this.errors.operator_type = this.validateOperatorType(value);
        break;
      case 'request_reason':
        this.errors.request_reason = this.validateRequestReason(
          value,
          this.isUpdateRequestMode(),
        );
        break;
    }
  }

  onFieldInput(fieldName: keyof typeof this.formData) {
    // Clear error when user starts typing
    this.errors[fieldName] = '';
  }

  isFormValid(): boolean {
    const emailError = this.validateEmail(this.formData.email, false);
    const phoneError = this.validatePhone(this.formData.phone, false);
    const emailOrPhoneError = this.validateEmailOrPhone();
    const requestReasonError = this.isUpdateRequestMode()
      ? this.validateRequestReason(this.formData.request_reason, true)
      : '';

    // Clear individual errors if email-or-phone requirement is the issue
    const hasEmailOrPhoneError = emailOrPhoneError !== '';

    return (
      this.validateName(this.formData.name) === '' &&
      this.validateFriendlyName(this.formData.friendly_name) === '' &&
      emailError === '' &&
      phoneError === '' &&
      emailOrPhoneError === '' &&
      this.validateAddress(this.formData.address) === '' &&
      this.validateOperator(this.formData.operator) === '' &&
      this.validateOperatorType(this.formData.operator_type) === '' &&
      requestReasonError === ''
    );
  }

  onSubmit(event: Event) {
    event.preventDefault();

    // Validate all fields
    this.errors.name = this.validateName(this.formData.name);
    this.errors.friendly_name = this.validateFriendlyName(
      this.formData.friendly_name,
    );
    this.errors.address = this.validateAddress(this.formData.address);
    this.errors.operator = this.validateOperator(this.formData.operator);
    this.errors.operator_type = this.validateOperatorType(
      this.formData.operator_type,
    );
    this.errors.request_reason = this.validateRequestReason(
      this.formData.request_reason,
      this.isUpdateRequestMode(),
    );

    // Validate email and phone individually (format only, not required)
    this.errors.email = this.validateEmail(this.formData.email, false);
    this.errors.phone = this.validatePhone(this.formData.phone, false);

    // Validate that at least one of email or phone is provided
    const emailOrPhoneError = this.validateEmailOrPhone();
    if (emailOrPhoneError) {
      // Show error on the field that's empty, or on both if both are empty
      if (!this.formData.email?.trim()) {
        this.errors.email = emailOrPhoneError;
      }
      if (!this.formData.phone?.trim()) {
        this.errors.phone = emailOrPhoneError;
      }
    }

    if (!this.isFormValid()) {
      return;
    }

    // Format phone numbers with country code
    this.formData.email = this.normalizeEmails(this.formData.email);
    this.formData.phone = this.normalizePhonesForInput(this.formData.phone);
    const formattedPhone = this.formatPhoneWithCountryCode(this.formData.phone);

    this.isSubmitting = true;

    // Check if we're in edit mode
    if (this.data && this.data.isEditMode && this.data.submissionId) {
      // Build update payload
      const updatePayload = {
        submission_id: String(this.data.submissionId),
        updated_row_data: {
          address: this.formData.address || '',
          email: this.formData.email || '',
          friendly_name: this.formData.friendly_name || '',
          name: this.formData.name || '',
          operator: this.formData.operator || '',
          operator_type: this.formData.operator_type || '',
          phone: formattedPhone || '',
        },
        request_reason: (this.formData.request_reason || '').trim(),
      };

      if (this.operatorLogoFile) {
        const formData = new FormData();
        formData.append('submission_id', String(this.data.submissionId));
        Object.entries(updatePayload.updated_row_data).forEach(
          ([key, value]) => {
            formData.append(`updated_row_data[${key}]`, String(value ?? ''));
          },
        );
        formData.append('request_reason', updatePayload.request_reason);
        formData.append('operator_logo', this.operatorLogoFile);

        this.contactDirectoryService.requestUpdateFormData(formData).subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            // Close dialog and return success
            this.dialogRef.close({ success: true, response, isEdit: true });
          },
          error: (error) => {
            console.error('Error updating entry:', error);
            this.isSubmitting = false;
            const errBody = error?.error;
            const errMsg =
              typeof errBody === 'string' ? errBody : errBody?.message || '';
            const errorMessage =
              errBody?.message_key ||
              (errMsg && errMsg.includes('2MB')
                ? 'Operator logo must not exceed 2MB.'
                : null) ||
              errBody?.message ||
              error?.message ||
              'Failed to update entry. Please try again.';
            alert(errorMessage);
          },
        });
        return;
      }

      this.contactDirectoryService.requestUpdate(updatePayload).subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          // Close dialog and return success
          this.dialogRef.close({ success: true, response, isEdit: true });
        },
        error: (error) => {
          console.error('Error updating entry:', error);
          this.isSubmitting = false;
          const errorMessage =
            error?.error?.message_key ||
            error?.error?.message ||
            error?.message ||
            'Failed to update entry. Please try again.';
          alert(errorMessage);
        },
      });
    } else {
      const entriesPayload = {
        name: this.formData.name || '',
        friendly_name: this.formData.friendly_name || '',
        email: this.formData.email || '',
        phone: formattedPhone || '',
        address: this.formData.address || '',
        operator: this.formData.operator || '',
        operator_type: this.formData.operator_type || '',
      };

      if (this.contributorImageFile) {
        const formData = new FormData();
        formData.append('entries', JSON.stringify(entriesPayload));
        formData.append('contributor_image', this.contributorImageFile);
        if (this.operatorLogoFile) {
          formData.append('operator_logo', this.operatorLogoFile);
        }

        this.contactDirectoryService.submitEntryFormData(formData).subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            this.dialogRef.close({ success: true, response, isEdit: false });
          },
          error: (error) => {
            console.error('Error submitting entry:', error);
            this.isSubmitting = false;
            const errBody = error?.error;
            const errMsg =
              typeof errBody === 'string' ? errBody : errBody?.message || '';
            const errorMessage =
              errBody?.message_key ||
              (errMsg && errMsg.includes('2MB')
                ? 'Contributor image must not exceed 2MB.'
                : null) ||
              errBody?.message ||
              error?.message ||
              'Failed to submit entry. Please try again.';
            alert(errorMessage);
          },
        });
      } else {
        if (this.operatorLogoFile) {
          const formData = new FormData();
          formData.append('entries', JSON.stringify(entriesPayload));
          formData.append('operator_logo', this.operatorLogoFile);
          this.contactDirectoryService.submitEntryFormData(formData).subscribe({
            next: (response: any) => {
              this.isSubmitting = false;
              this.dialogRef.close({ success: true, response, isEdit: false });
            },
            error: (error) => {
              console.error('Error submitting entry:', error);
              this.isSubmitting = false;
              const errorMessage =
                error?.error?.message_key ||
                error?.error?.message ||
                error?.message ||
                'Failed to submit entry. Please try again.';
              alert(errorMessage);
            },
          });
          return;
        }
        this.contactDirectoryService.submitEntry({ entries: entriesPayload }).subscribe({
          next: (response: any) => {
            this.isSubmitting = false;
            this.dialogRef.close({ success: true, response, isEdit: false });
          },
          error: (error) => {
            console.error('Error submitting entry:', error);
            this.isSubmitting = false;
            const errorMessage =
              error?.error?.message_key ||
              error?.error?.message ||
              error?.message ||
              'Failed to submit entry. Please try again.';
            alert(errorMessage);
          },
        });
      }
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  resetForm() {
    this.formData = {
      name: '',
      friendly_name: '',
      email: '',
      phone: '',
      address: '',
      operator: '',
      operator_type: '',
      request_reason: 'Updating contact information with latest details',
    };
    this.errors = {
      name: '',
      friendly_name: '',
      email: '',
      phone: '',
      address: '',
      operator: '',
      operator_type: '',
      contributorImage: '',
      operatorLogo: '',
      request_reason: '',
    };
    this.clearContributorImage();
    this.clearOperatorLogo();
  }

  formatPhoneNumbers(value: string): string {
    return this.normalizePhonesForInput(value);
  }

  onPhoneInput(event: any) {
    const typedValue = (event?.target?.value ?? '').toString();
    const sanitized = typedValue
      .split(',')
      .map((part: string) => part.replace(/[^\d+]/g, ''))
      .join(',');
    this.formData.phone = sanitized;
    event.target.value = sanitized;
    this.onFieldInput('phone');
  }

  onPhoneKeyPress(event: KeyboardEvent): boolean {
    // Allow: digits (0-9), comma, backspace, delete, tab, escape, enter, and arrow keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    // Allow control keys (Ctrl, Alt, Meta)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return true;
    }

    // Allow allowed special keys
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    // Allow digits (0-9), comma and plus (+)
    const char = event.key;
    if ((char >= '0' && char <= '9') || char === ',' || char === '+') {
      return true;
    }

    // Prevent all other characters
    event.preventDefault();
    return false;
  }

  onEmailInput(event: any) {
    this.formData.email = (event?.target?.value ?? '').toString().toLowerCase();
    this.onFieldInput('email');
  }

  private validateContributorImageFile(file: File): string {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please choose a JPEG, PNG, WebP or GIF image.';
    }
    if (file.size > this.CONTRIBUTOR_IMAGE_MAX_SIZE_BYTES) {
      return 'Contributor image must not exceed 2MB.';
    }
    return '';
  }

  private validateOperatorLogoFile(file: File): string {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please choose a JPEG, PNG, WebP or GIF image.';
    }
    if (file.size > this.CONTRIBUTOR_IMAGE_MAX_SIZE_BYTES) {
      return 'Operator logo must not exceed 2MB.';
    }
    return '';
  }

  onContributorImageChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.errors.contributorImage = this.validateContributorImageFile(file);
    if (this.errors.contributorImage) {
      this.contributorImagePreview = null;
      this.contributorImageFile = null;
      input.value = '';
      this.cdr.detectChanges();
      return;
    }
    this.contributorImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.contributorImagePreview = reader.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  onContributorImageDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.errors.contributorImage = this.validateContributorImageFile(file);
    if (this.errors.contributorImage) {
      this.contributorImagePreview = null;
      this.contributorImageFile = null;
      this.cdr.detectChanges();
      return;
    }
    this.contributorImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.contributorImagePreview = reader.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  clearContributorImage() {
    this.contributorImagePreview = null;
    this.contributorImageFile = null;
    this.errors.contributorImage = '';
    this.cdr.detectChanges();
  }

  onOperatorLogoChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.errors.operatorLogo = this.validateOperatorLogoFile(file);
    if (this.errors.operatorLogo) {
      this.operatorLogoPreview = null;
      this.operatorLogoFile = null;
      input.value = '';
      this.cdr.detectChanges();
      return;
    }
    this.operatorLogoFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.operatorLogoPreview = reader.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  onOperatorLogoDrop(event: DragEvent) {
    event.preventDefault();
    this.isOperatorLogoDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.errors.operatorLogo = this.validateOperatorLogoFile(file);
    if (this.errors.operatorLogo) {
      this.operatorLogoPreview = null;
      this.operatorLogoFile = null;
      this.cdr.detectChanges();
      return;
    }
    this.operatorLogoFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.operatorLogoPreview = reader.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  clearOperatorLogo() {
    this.operatorLogoPreview = null;
    this.operatorLogoFile = null;
    this.errors.operatorLogo = '';
    this.cdr.detectChanges();
  }

  formatPhoneWithCountryCode(phoneValue: string): string {
    if (!phoneValue || phoneValue.trim().length === 0) {
      return '';
    }
    const phones = phoneValue
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    const formattedSet = new Set<string>();
    for (const phone of phones) {
      const cleaned = phone.replace(/[^\d+]/g, '');
      if (!cleaned) continue;

      if (cleaned.startsWith('+')) {
        const normalized = `+${cleaned.slice(1).replace(/\D/g, '')}`;
        if (normalized.length >= 8 && normalized.length <= 16) {
          formattedSet.add(normalized);
        }
        continue;
      }

      const digits = cleaned.replace(/\D/g, '');
      if (!digits) continue;
      const countryCode = this.selectedCountryCode || '+91';
      formattedSet.add(`${countryCode}${digits}`);
    }

    return Array.from(formattedSet).join(',');
  }

  private normalizeEmails(value: any): string {
    const raw = this.asCommaSeparated(value);
    const unique = new Set(
      raw
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e.length > 0),
    );
    return Array.from(unique).join(', ');
  }

  private normalizePhonesForInput(value: any): string {
    const raw = this.asCommaSeparated(value);
    const unique = new Set(
      raw
        .split(',')
        .map((p) => p.trim())
        .map((p) => p.replace(/[^\d,+]/g, ''))
        .map((p) => {
          if (!p) return '';
          if (p.startsWith('+')) {
            return `+${p.slice(1).replace(/\D/g, '')}`;
          }
          return p.replace(/\D/g, '');
        })
        .filter((p) => p.length > 0),
    );
    return Array.from(unique).join(',');
  }

  private asCommaSeparated(value: any): string {
    if (Array.isArray(value)) {
      return value
        .map((item) => (item ?? '').toString().trim())
        .filter((item) => item.length > 0)
        .join(',');
    }
    return (value ?? '').toString();
  }

  private applyPhonePrefill(phoneValue: any): void {
    const normalized = this.normalizePhonesForInput(phoneValue);
    if (!normalized) {
      this.selectedCountryCode = '+91';
      this.formData.phone = '';
      return;
    }

    const first = normalized.split(',')[0]?.trim() || '';
    if (first.startsWith('+')) {
      this.selectedCountryCode = this.getBestMatchingCountryCode(first);
    } else {
      this.selectedCountryCode = '+91';
    }

    this.formData.phone = this.stripSelectedCountryCodeFromPhones(
      normalized,
      this.selectedCountryCode,
    );
  }

  private getBestMatchingCountryCode(phoneWithCode: string): string {
    const sortedCountries = [...this.countries].sort(
      (a: any, b: any) =>
        (b?.dialCode?.length || 0) - (a?.dialCode?.length || 0),
    );

    for (const country of sortedCountries) {
      const code = (country?.dialCode || '').toString();
      if (code && phoneWithCode.startsWith(code)) {
        return code;
      }
    }

    return '+91';
  }

  private stripSelectedCountryCodeFromPhones(
    value: string,
    countryCode: string,
  ): string {
    const phones = value
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    const stripped = phones.map((phone) => {
      if (phone.startsWith(countryCode)) {
        return phone.slice(countryCode.length);
      }
      return phone;
    });

    return stripped.join(',');
  }
}
